// src/components/Team/userCard/UseTeamDataStandalone.jsx
// ✅ VERSION INDÉPENDANTE DU CONTEXTE
// Peut être utilisé dans Team.jsx OU Subscriptions.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Axios from "../../../_services/caller.service";

export function useTeamDataStandalone({ ownerId, userAuth = true }) {
  const { t } = useTranslation();
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingInvites, setIsLoadingInvites] = useState(true);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);

  // ROLE
  const [userRole, setUserRole] = useState([]);
  const [updateRole, setUpdateRole] = useState([]);
  const [majRole, setMajRole] = useState(false);

  // USERS + INVITES
  const [inviteUser, setInviteUser] = useState([]);
  const [inviteWaiting, setInviteWaiting] = useState([]);
  const [newInvite, setNewInvite] = useState(0);

  // 🆕 AUTO-FETCH inviteId depuis la liste des users
  const [inviteIds, setInviteIds] = useState([]);

  // reload flags
  const [maj, setMaj] = useState(false);
  const [reloadWaitingInvite, setReloadWaitingInvite] = useState("");

  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // ===== fetch users FIRST
  useEffect(() => {
    if (!ownerId) return;
    if (!initialLoadDone) setIsLoadingUsers(true);
    Axios.get(`/getAuthCabineUser/${ownerId}`)
      .then((res) => {
        const users = res.data.data || [];
        setInviteUser(users);
        // 🆕 Extract inviteIds from users
        const ids = users.map((u) => u.ID).filter(Boolean);
        setInviteIds(ids);
      })
      .catch(console.log)
      .finally(() => {
        setIsLoadingUsers(false);
        setInitialLoadDone(true);
      });
  }, [ownerId, maj]);

  // ===== fetch roles BASED ON inviteIds
  useEffect(() => {
    if (!ownerId) return;
    if (!userAuth) return;
    if (!Array.isArray(inviteIds) || inviteIds.length === 0) {
      setUserRole([]);
      setIsLoadingRoles(false);
      return;
    }

    if (!initialLoadDone) setIsLoadingRoles(true);
    Axios.post(`/userRole/${ownerId}`, { data: inviteIds })
      .then((res) => setUserRole(res.data.data || []))
      .catch(console.log)
      .finally(() => setIsLoadingRoles(false));
  }, [ownerId, inviteIds, userAuth, majRole]);

  // ===== fetch invites waiting
  useEffect(() => {
    if (!ownerId) return;
    setIsLoadingInvites(true);
    Axios.get(`/inviteWaiting/${ownerId}`)
      .then((res) => setInviteWaiting(res.data.data || []))
      .catch(console.log)
      .finally(() => setIsLoadingInvites(false));
  }, [ownerId, newInvite, reloadWaitingInvite]);

  // ===== Helper functions
  const displayRoleByUser = useCallback(
    (userId) => {
      const role = userRole
        .filter((r) => String(userId) === String(r.user))
        .map((c) => c.role)[0];

      if (role === 8) return t("roles.calibration");
      if (role === 1) return t("roles.consultation");
      if (role === 256) return t("roles.admin");
      return t("team.no_right");
    },
    [userRole]
  );

  const selectDefaultValue = useCallback(
    (userId) => {
      const role = userRole
        .filter((r) => String(userId) === String(r.user))
        .map((c) => c.role)[0];

      return role || 0;
    },
    [userRole]
  );

  const numberCabsAuth = useCallback(
    (userId) => {
      const userObj = inviteUser.find((u) => String(u.ID) === String(userId));
      const list = userObj?.cabines?.[0] || [];
      return list.reduce((acc, cab) => acc + (cab?.auth ? 1 : 0), 0);
    },
    [inviteUser]
  );

  // ===== Permission Actions
  const setAllCabPermissions = useCallback(
    async ({ userId, checked }) => {
      if (!ownerId || !userId) return;

      try {
        if (checked) {
          await Axios.post(`/createPermissionsAllCab/${ownerId}`, {
            data: { updateUserId: userId },
          });
        } else {
          await Axios.delete(`/deletePermissionsAllCab/${ownerId}?userId=${userId}`);
        }
        // refresh
        setMajRole((v) => !v);
        setMaj((v) => !v);
      } catch (err) {
        console.error("❌ Error setting all cab permissions:", err);
      }
    },
    [ownerId]
  );

  const setCabPermission = useCallback(
    async ({ userId, cabId, checked }) => {
      if (!ownerId || !userId || !cabId) return;

      try {
        if (checked) {
          await Axios.post(`/createPermissionsCabByCab/${ownerId}`, {
            updateUserId: userId,
            cabId,
            role: updateRole,
          });
        } else {
          await Axios.delete(
            `/deletePermissionsCabByCab/${ownerId}?userId=${userId}&cabid=${cabId}`
          );
        }
        // refresh
        setMajRole((v) => !v);
        setMaj((v) => !v);
      } catch (err) {
        console.error("❌ Error setting cab permission:", err);
      }
    },
    [ownerId, updateRole]
  );

  // ===== Counts and previews
  const invitesCount = Array.isArray(inviteWaiting) ? inviteWaiting.length : 0;
  const usersCount = Array.isArray(inviteUser) ? inviteUser.length : 0;

  const previewUsers = useMemo(() => {
    if (!Array.isArray(inviteUser)) return [];
    return inviteUser.slice(0, 3);
  }, [inviteUser]);

  return {
    // loading
    isLoadingUsers,
    isLoadingInvites,
    isLoadingRoles,
    isLoading: isLoadingUsers || isLoadingInvites || isLoadingRoles,

    // data
    inviteUser,
    inviteWaiting,
    userRole,
    inviteIds, // 🆕 exposé si tu en as besoin

    // counts
    invitesCount,
    usersCount,
    previewUsers,

    // role management
    updateRole,
    setUpdateRole,
    majRole,
    setMajRole,

    // reload flags
    maj,
    setMaj,
    reloadWaitingInvite,
    setReloadWaitingInvite,
    newInvite,
    setNewInvite,

    // data setters
    setInviteUser,
    setInviteWaiting,
    setUserRole,

    // helpers
    displayRoleByUser,
    selectDefaultValue,
    numberCabsAuth,

    // permission actions
    setAllCabPermissions,
    setCabPermission,
  };
}
